import { useState, useEffect, useRef } from "react";
import PlainTextExpandingInput from "./PTS/PlainTextExpandingInput";

// Same red EditableRow.jsx uses for "Ei tiedossa" — empty fields across
// the app read as "missing" consistently with this one color.
export const EMPTY_COLOR = "#dc3545";

// Hover-to-edit text field: plain text at rest, becomes an editable
// control on click. Standalone/reusable (originally built inside
// MaaraaikaistarkastusTaulukko.jsx, extracted here once a second table
// needed the identical behavior) — no per-field API call, just { value,
// onChange }, so any caller's own commit/debounce logic (e.g. "commit
// the whole data blob on blur") keeps working unchanged. Collapsing back
// to read mode uses a click-outside listener rather than onBlur —
// Lexical's ContentEditable (used for the `lexical` variant) doesn't
// reliably bubble a native blur the way a plain <input> does.
export default function HoverTextField({ value, onChange, placeholder, lexical, namespace, bold, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const wrapRef = useRef(null);

  // Shared by click-outside, Enter on the plain <input>, and Enter inside
  // the lexical editor — same exit path for every variant. Explicitly
  // blurs whatever's actually focused inside before swapping back to read
  // mode: just calling setEditing(false) does NOT reliably produce a real
  // blur event on its own (removing a focused element from the DOM via a
  // React re-render isn't the same as it genuinely losing focus while
  // still mounted), and the caller's own commit-on-blur logic
  // (MaaraaikaistarkastusTaulukko.jsx / RakennuslupaTaulukko.jsx's
  // handleBlur, bound to the outer table's onBlur) depends on a real
  // blur/focusout bubbling up to it — confirmed broken without this, for
  // every exit path and both the plain-input and lexical variants.
  const commitAndExit = () => {
    const active = wrapRef.current?.querySelector("input, [contenteditable='true']");
    active?.blur();
    setEditing(false);
  };

  useEffect(() => {
    if (!editing) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) commitAndExit();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editing]);

  // Plain, non-interactive text — used when browsing a historical
  // (non-current) Määräaikaistarkastustaulukko version, which must never
  // let the user edit the past.
  if (readOnly) {
    return (
      <div style={{ fontWeight: bold ? 600 : 400 }}>
        {value ? value : (
          <span style={{ color: EMPTY_COLOR, fontWeight: 600 }}>{placeholder}</span>
        )}
      </div>
    );
  }

  if (editing) {
    return (
      <div ref={wrapRef}>
        {lexical ? (
          <PlainTextExpandingInput namespace={namespace} value={value} onChange={onChange} placeholder={placeholder}
            onEnter={commitAndExit} />
        ) : (
          <input autoFocus className="form-control form-control-sm" value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commitAndExit(); } }}
            placeholder={placeholder} />
        )}
      </div>
    );
  }
  return (
    <>
      <style>{`
        .hover-text-field { cursor: pointer; border-radius: 6px; padding: 2px 4px; margin: -2px -4px; transition: background-color 0.12s ease, font-size 0.12s ease; }
        .hover-text-field:hover { background-color: #eafbf1; font-size: 1.1em; }
      `}</style>
      <div className="hover-text-field" role="button" tabIndex={0} onClick={() => setEditing(true)}
        style={{ fontWeight: bold ? 600 : 400 }}>
        {value ? value : (
          <span style={{ color: EMPTY_COLOR, fontWeight: 600 }}>
            <i className="bi bi-exclamation-circle me-1" style={{ fontSize: "0.85em" }} />{placeholder}
          </span>
        )}
      </div>
    </>
  );
}
